'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  primaryInterest: string;
  termsAccepted: boolean;
}

interface SignUpFormProps {
  onSwitchMode: () => void;
}

const interests = [
  'Art & Collections',
  'Rare Timepieces',
  'Private Real Estate',
  'Wine & Provenance',
  'Extraordinary Places',
  'Rare Automobiles',
  'Jewellery & Gemstones',
  'Collector Markets',
];

export default function SignUpForm({ onSwitchMode }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        primaryInterest: data.primaryInterest,
      });
      toast.success(`Welcome, ${data.firstName}. Your membership is active.`);
      router.push('/onboarding');
    } catch (err: any) {
      setError('email', {
        message: err?.message || 'Could not create account. Please try again.',
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-slide-up">
      <div className="mb-8">
        <h1 className="font-editorial text-2xl font-semibold text-cream">
          Apply for Membership
        </h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Access is exclusive. Every member is a connoisseur. Begin your private world of discovery.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Name Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-first" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
              First Name
            </label>
            <input
              id="signup-first"
              type="text"
              autoComplete="given-name"
              placeholder="First"
              className="input-luxury w-full px-4 py-3 rounded-sm text-sm"
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && (
              <p className="text-red-400 text-xs mt-1">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="signup-last" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
              Last Name
            </label>
            <input
              id="signup-last"
              type="text"
              autoComplete="family-name"
              placeholder="Last"
              className="input-luxury w-full px-4 py-3 rounded-sm text-sm"
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && (
              <p className="text-red-400 text-xs mt-1">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
            Email Address
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            className="input-luxury w-full px-4 py-3 rounded-sm text-sm"
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
            Create Passphrase
          </label>
          <p className="text-xs text-muted-foreground mb-2">Minimum 8 characters</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Your secure passphrase"
              className="input-luxury w-full px-4 py-3 pr-12 rounded-sm text-sm"
              {...register('password', {
                required: 'Passphrase is required',
                minLength: { value: 8, message: 'Minimum 8 characters required' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide passphrase' : 'Show passphrase'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-cream transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        {/* Primary Interest */}
        <div>
          <label htmlFor="signup-interest" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
            Primary Collecting Interest
          </label>
          <select
            id="signup-interest"
            className="input-luxury w-full px-4 py-3 rounded-sm text-sm appearance-none cursor-pointer"
            {...register('primaryInterest', { required: 'Please select an interest' })}
          >
            <option value="" disabled>Select your primary interest</option>
            {interests.map((interest) => (
              <option key={`interest-${interest.replace(/\s+/g, '-').toLowerCase()}`} value={interest}>
                {interest}
              </option>
            ))}
          </select>
          {errors.primaryInterest && (
            <p className="text-red-400 text-xs mt-1">{errors.primaryInterest.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded-sm border border-muted bg-input accent-gold flex-shrink-0"
              {...register('termsAccepted', { required: 'You must accept the terms to become a member' })}
            />
            <span className="text-xs text-muted-foreground group-hover:text-cream/80 transition-colors leading-relaxed">
              I accept the{' '}
              <button type="button" className="text-gold hover:text-gold-light underline underline-offset-2">
                Terms of Membership
              </button>{' '}
              and{' '}
              <button type="button" className="text-gold hover:text-gold-light underline underline-offset-2">
                Privacy Policy
              </button>
              . I understand this is a subscription that renews annually.
            </span>
          </label>
          {errors.termsAccepted && (
            <p className="text-red-400 text-xs mt-1">{errors.termsAccepted.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 gold-gradient-bg text-obsidian font-semibold text-sm tracking-widest uppercase rounded-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-obsidian/30 border-t-obsidian rounded-full animate-spin" />
              <span>Creating Membership...</span>
            </>
          ) : (
            <>
              <span>Begin Membership</span>
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-5">
        Already a member?{' '}
        <button onClick={onSwitchMode} className="text-gold hover:text-gold-light transition-colors">
          Sign in
        </button>
      </p>
    </div>
  );
}